import { ChevronDownIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';
import type { MouseEventHandler } from 'react';
import React from 'react';

import { Restricted } from 'containers/Restricted';

import { OrgNavLink } from 'components/ui/atoms/OrgNavLink';

import type { SidebarMenuItem } from './SidebarItem';
import { isSidebarMenuSubItem } from './SidebarItem';

type Props = {
  isExpanded: boolean;
  item: SidebarMenuItem;
  onClick?: MouseEventHandler<HTMLElement>;
  onSubItemClick?: MouseEventHandler<HTMLElement>;
  sidebarOpen?: boolean;
  isActive: boolean;
  'data-testid'?: string;
};

export const SidebarDropdownItem = ({
  isExpanded,
  item,
  onClick,
  onSubItemClick,
  isActive,
  sidebarOpen = true,
  'data-testid': dataTestId
}: Props) => {
  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        id={item.id}
        data-testid={dataTestId}
        aria-label={item.title}
        aria-expanded={isExpanded}
        className={cn(
          'group flex h-11 w-full items-center rounded-full px-3 text-sm font-medium hover:bg-gray-100',
          {
            'gap-3': sidebarOpen,
            'justify-center px-0': !sidebarOpen,
            'text-content-brand': isActive,
            'bg-surface-selected': !sidebarOpen && isActive
          }
        )}
      >
        <Icon
          className={cn('size-5 shrink-0', {
            'stroke-content-brand': isActive
          })}
        />
        {sidebarOpen ? (
          <span
            className={cn(
              'whitespace-nowrap text-black',
              isActive ? 'font-medium text-content-brand' : 'font-normal'
            )}
          >
            {item.title}
          </span>
        ) : (
          <span className="sr-only">{item.title}</span>
        )}
        {sidebarOpen && (
          <ChevronDownIcon
            className={cn('ml-auto size-4', {
              'rotate-180': isExpanded
            })}
          />
        )}
      </button>

      {sidebarOpen && (
        <div
          role="tabpanel"
          aria-hidden={!isExpanded}
          className={cn(
            'mt-1 gap-y-4 space-y-1 border-l border-surface-muted pl-4',
            {
              'max-h-0 overflow-hidden opacity-0': !isExpanded,
              'max-h-full opacity-100': isExpanded
            }
          )}
        >
          {item.subItems?.map((subItem) => {
            if (isSidebarMenuSubItem(subItem)) {
              return (
                <Restricted
                  requiredPermission={subItem.requiredPermission}
                  key={subItem.id ?? subItem['data-testid']}
                >
                  <OrgNavLink
                    to={subItem.path}
                    onClick={onSubItemClick}
                    className={({ isActive }) =>
                      cn(
                        'flex h-10 items-center rounded-full px-7 text-sm text-black hover:bg-gray-100',
                        {
                          'bg-surface-selected': isActive
                        }
                      )
                    }
                  >
                    <span
                      data-testid={subItem['data-testid']}
                      className="whitespace-nowrap"
                    >
                      {subItem.title}
                    </span>
                    {subItem.subtitle && (
                      <div className="ml-2 text-xs text-highlight-strong">
                        {subItem.subtitle}
                      </div>
                    )}
                  </OrgNavLink>
                </Restricted>
              );
            } else {
              return subItem;
            }
          })}
        </div>
      )}
    </div>
  );
};
